import React, { useCallback, useEffect, useRef, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import { Button } from "@/components/common/Button";
import { Tooltip } from "@/components/common/Tooltip";
import { DatasetMetaSection, MetaAddButton } from "./DatasetMetaSection";

const BOX_CLASS =
  "w-full rounded-app border px-2 py-1.5 leading-5 text-fg-medium";

interface DescriptionSectionProps {
  description: string | null;
  canEdit: boolean;
  onSave: (next: string | null) => void;
  saving?: boolean;
  error?: boolean;
  placeholder?: string;
  addLabel?: string;
  label?: string;
  icon?: React.ReactNode;
  headingPl?: string;
  bodyPl?: string;
  pr?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export const DescriptionSection: React.FC<DescriptionSectionProps> = ({
  description,
  canEdit,
  onSave,
  saving = false,
  error = false,
  placeholder = "Add a description…",
  addLabel = "Add a description",
  label = "Description",
  icon,
  headingPl,
  bodyPl,
  pr,
  collapsible,
  defaultCollapsed,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(description ?? "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback((el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, []);

  useEffect(() => {
    if (!editing) setDraft(description ?? "");
  }, [editing, description]);

  useEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    autoResize(el);
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [editing, autoResize]);

  const save = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    const next = trimmed ? trimmed : null;
    if (next === (description ?? null)) return;
    onSave(next);
  }, [draft, description, onSave]);

  if (!canEdit && !description && !editing) return null;

  const saveStatus =
    saving || error ? (
      <span
        className={error ? "text-error" : "text-fg-muted"}
        style={{ fontSize: "var(--font-size-x-small)" }}
      >
        {error ? "Failed to save" : "Saving…"}
      </span>
    ) : undefined;

  return (
    <DatasetMetaSection
      icon={icon ?? <NotesOutlinedIcon className="icon-sm" />}
      label={label}
      headingPl={headingPl}
      bodyPl={bodyPl}
      pr={pr}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
      itemCount={editing || description ? 1 : 0}
      meta={saveStatus}
      action={
        canEdit && !editing && !description ? (
          <MetaAddButton label={addLabel} onClick={() => setEditing(true)} />
        ) : undefined
      }
    >
      {editing ? (
        <textarea
          ref={textareaRef}
          className={`${BOX_CLASS} resize-none overflow-y-auto max-h-40 bg-transparent focus:outline-none`}
          style={{ borderColor: "var(--color-accent)" }}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            autoResize(e.target);
          }}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Escape") setEditing(false);
          }}
          placeholder={placeholder}
          rows={1}
        />
      ) : description ? (
        <div className="relative group/desc">
          <div
            className={`${BOX_CLASS} border-transparent whitespace-pre-wrap break-words overflow-y-auto max-h-40`}
          >
            {description}
          </div>
          {canEdit && (
            <div className="absolute top-1 right-1 opacity-0 transition-opacity group-hover/desc:opacity-100 focus-within:opacity-100">
              <Tooltip title={`Edit ${label.toLowerCase()}`} placement="top">
                <Button
                  variant="default"
                  size="xs"
                  aria-label={`Edit ${label.toLowerCase()}`}
                  onClick={() => setEditing(true)}
                >
                  <EditOutlinedIcon />
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      ) : null}
    </DatasetMetaSection>
  );
};
